/**
 * Shared hooks for TORI Web Agent
 * 
 * These hooks encapsulate reusable auth and iframe messaging logic.
 */

import { useEffect, useCallback, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import type { AuthenticationResult, PopupRequest } from "@azure/msal-browser";
import { createAuthPayload, type ToriWidgetCoreProps } from "./types";

/**
 * Login request scopes - centralized for all MSAL operations
 */
export const LOGIN_REQUEST: PopupRequest = {
  scopes: ["User.Read"],
};

/**
 * Hook to manage TORI authentication state and token acquisition
 */
export function useToriAuth() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const [authResult, setAuthResult] = useState<AuthenticationResult | null>(null);

  // Acquire token on authentication
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const accounts = instance.getAllAccounts();
    const activeAccount = instance.getActiveAccount() || accounts[0];
    if (!activeAccount) return;

    let cancelled = false;
    
    instance
      .acquireTokenSilent({
        ...LOGIN_REQUEST,
        account: activeAccount,
      })
      .then((result) => {
        if (!cancelled) setAuthResult(result);
      })
      .catch(async () => {
        try {
          const result = await instance.acquireTokenPopup({
            ...LOGIN_REQUEST,
            account: activeAccount,
          });
          if (!cancelled) setAuthResult(result);
        } catch (e) {
          console.error("Token acquisition failed", e);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, instance]);

  // Login handler
  const login = useCallback(async () => {
    try {
      const result = await instance.loginPopup(LOGIN_REQUEST);
      setAuthResult(result);
      return result;
    } catch (e) {
      console.error("Login failed", e);
      return null;
    }
  }, [instance]);

  // Force refresh tokens
  const refreshTokens = useCallback(async (): Promise<AuthenticationResult | null> => {
    const accounts = instance.getAllAccounts();
    const activeAccount = instance.getActiveAccount() || accounts[0];
    if (!activeAccount) return null;

    try {
      const result = await instance.acquireTokenSilent({
        ...LOGIN_REQUEST,
        account: activeAccount,
        forceRefresh: true,
      });
      setAuthResult(result);
      return result;
    } catch (silentError) {
      console.warn("Silent token refresh failed, trying popup", silentError);
      try {
        const result = await instance.acquireTokenPopup({
          ...LOGIN_REQUEST,
          account: activeAccount,
        });
        setAuthResult(result);
        return result;
      } catch (popupError) {
        console.error("Token refresh failed", popupError);
        return null;
      }
    }
  }, [instance]);

  return {
    isAuthenticated,
    inProgress,
    authResult,
    login,
    refreshTokens,
  };
}

/**
 * Hook to manage iframe messaging for TORI
 */
export function useIframeMessaging(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  iframeOrigin: string,
  authResult: AuthenticationResult | null,
  hostContext: ToriWidgetCoreProps["hostContext"],
  refreshTokens: () => Promise<AuthenticationResult | null>,
  prompts?: string[]
) {
  // Send auth tokens to iframe
  const sendAuthTokens = useCallback(
    (result: AuthenticationResult) => {
      const payload = createAuthPayload(result, hostContext, prompts);
      try {
        iframeRef.current?.contentWindow?.postMessage(payload, iframeOrigin);
        console.log("Auth tokens sent to iframe");
      } catch (e) {
        console.error("postMessage failed", e);
      }
    },
    [iframeRef, iframeOrigin, hostContext, prompts]
  );

  // Listen for iframe messages
  useEffect(() => {
    if (!authResult) return;

    const handleMessage = async (event: MessageEvent) => {
      // Verify it's from our iframe
      if (event.source !== iframeRef.current?.contentWindow) return;

      if (event.data?.type === "TORI_READY") {
        console.log("Child iframe is ready, sending auth tokens");
        sendAuthTokens(authResult);
      }

      if (event.data?.type === "TORI_TOKEN_REFRESH") {
        console.log("Token refresh requested by iframe", event.data.reason);
        const freshResult = await refreshTokens();
        if (freshResult) {
          console.log("Sending refreshed tokens to iframe");
          sendAuthTokens(freshResult);
        } else {
          console.error("Failed to refresh tokens for iframe");
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [authResult, iframeRef, sendAuthTokens, refreshTokens]);

  return { sendAuthTokens };
}
